import { DeleteFilled, EditFilled } from "@ant-design/icons";
import { Button, Flex, Spin } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDeleteSavings } from "../../api/api.delete";
import {
  useGetCurrencyRates,
  useGetGroups2,
  useGetSavings,
  useGetSavingsTotal2,
} from "../../api/api.get";
import ConfirmDialog from "../../components/dialog/confirm.dialog";
import GroupDialog from "../../components/dialog/group.dialog";
import CreateSavingsGoalDialog from "../../components/dialog/savings.goal.dialog";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { getCurrencySymbolPerCountry } from "../../helpers/currency.helper";
import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { IGroup2, ISavings } from "../../types/types";
import { EXHUT_SAVINGS_GOAL_TOTAL } from "../../utils/constants";
import { capitalize } from "../../utils/functions";
import { toFixedDisplay } from "../../utils/number.utils";
import CreateSavingsDialog from "./create.savings.dialog";
import EditSavingsDialog from "./edit.savings.dialog";
import SavingsGoalsSection from "../savings-goals/savings.goals.section";

export default function Savings() {
  const { defaultGroupId } = useGroupContext();
  const { data: groupsData } = useGetGroups2();
  const { user } = useAuthContext();

  const { notification, notifySuccess, notifyError } = useNotifications();

  const { mutateAsync: deleteAsync } = useDeleteSavings(defaultGroupId);

  const currentGroup: IGroup2 | undefined = useMemo(() => {
    if (defaultGroupId && groupsData) {
      return groupsData.find((g) => g.id === defaultGroupId);
    }
    return undefined;
  }, [defaultGroupId, groupsData]);

  const {
    data: savingsData,
    isPending: isSavingsDataLoading,
    refetch,
  } = useGetSavings(defaultGroupId);
  const {
    data: savingsTotal,
    isPending: isSavingsTotalLoading,
    refetch: refetchTotal,
  } = useGetSavingsTotal2(defaultGroupId);

  const { data: currencyRatesData, isPending: isCurrencyRatesDataLoading } =
    useGetCurrencyRates(user?.country_code);

  const { currency } = useCurrencyContext();

  const [savingsGoal, setSavingaGoal] = useState<number>(0);

  useEffect(() => {
    const total = localStorage.getItem(EXHUT_SAVINGS_GOAL_TOTAL);
    setSavingaGoal(total && !isNaN(+total) ? +total : 0);
  }, []);

  const savingsPercentage = savingsGoal
    ? ((savingsTotal?.total ?? 0) / savingsGoal) * 100
    : 0;

  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);

  const [deletableSavingsId, setDeletableSavingsId] = useState<
    string | undefined
  >();

  const handleDeleteSavings = () => {
    deleteAsync({ savingsId: deletableSavingsId! })
      .then(() => {
        notifySuccess({ description: "Deleted successfully" });
        setDeletableSavingsId(undefined);
        refetch();
      })
      .catch(() => notifyError("Whoops! Please try again."));
  };

  const [showSavingsGoalDialog, setShowSavingsGoalDialog] =
    useState<boolean>(false);

  const [groupsDialogVisible, showGroupsDialog] = useState(false);

  useEffect(() => {
    if (!currentGroup && groupsData && groupsData.length > 0) {
      showGroupsDialog(true);
    } else {
      showGroupsDialog(false);
    }
  }, [currentGroup, groupsData]);

  const [editSavingsRef, setEditSavingsRef] = useState<ISavings>();

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}
      <main className="pt-8 pb-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {groupsDialogVisible && (
            <GroupDialog
              open={groupsDialogVisible}
              onCancel={() => showGroupsDialog(false)}
              onOk={() => showGroupsDialog(false)}
            />
          )}

          {showAddDialog && (
            <CreateSavingsDialog
              open={showAddDialog}
              onCancel={() => setShowAddDialog(false)}
              onSuccess={() => {
                refetch();
                refetchTotal();
                setShowAddDialog(false);
              }}
            />
          )}

          {editSavingsRef && (
            <EditSavingsDialog
              open={!!editSavingsRef}
              savings={editSavingsRef}
              onCancel={() => setEditSavingsRef(undefined)}
              onSuccess={() => {
                setEditSavingsRef(undefined);
                refetchTotal();
                refetch();
              }}
              onError={() => {
                setEditSavingsRef(undefined);
                notifyError("Whoops! please try again.");
              }}
            />
          )}

          {showSavingsGoalDialog && (
            <CreateSavingsGoalDialog
              onCancel={() => setShowSavingsGoalDialog(false)}
              onSuccess={(value) => {
                setSavingaGoal(value);
                setShowSavingsGoalDialog(false);
              }}
              open={showSavingsGoalDialog}
            />
          )}
          {!!deletableSavingsId && (
            <ConfirmDialog
              open={!!deletableSavingsId}
              onCancel={() => setDeletableSavingsId(undefined)}
              onOk={handleDeleteSavings}
              title="Are you sure you would lik to delete this savings?"
            />
          )}

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Savings</h1>

            <div className="flex justify-between">
              <p className="text-muted-foreground">
                Track and grow your savings with confidence
              </p>
            </div>
          </div>

          {/* Two Panel Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Left Panel: Total Savings Overview */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Total Savings
                </CardTitle>
                <CardDescription>Your savings overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Total Amount */}
                  <div className="border-b pb-6">
                    {isSavingsTotalLoading && <Spin size="large" />}
                    {!isSavingsTotalLoading && (
                      <>
                        <p className="text-sm text-muted-foreground mb-2">
                          Current Balance
                        </p>
                        <p className="text-4xl font-bold text-foreground mb-2">
                          {currency}
                          {toFixedDisplay(savingsTotal?.total)}
                        </p>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${savingsPercentage}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {savingsPercentage.toFixed(1)}% of $
                          <button
                            className="text-xs border-0 underline text-blue-500 ml-1"
                            onClick={() => setShowSavingsGoalDialog(true)}
                          >
                            {savingsGoal.toFixed(2)} goal
                          </button>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Right Panel: Savings List */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Savings
                </CardTitle>
                <CardDescription>
                  <div className="flex justify-between">
                    Your savings accounts and balances
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setShowAddDialog(!showAddDialog)}
                    >
                      Add
                    </Button>
                  </div>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {isSavingsDataLoading && <Spin size="large" />}
                  {savingsData?.map((savings) => (
                    <div
                      key={savings.id}
                      className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold text-foreground">
                            {capitalize(savings.description)}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            {dayjs(savings.savingsDate).format("DD MMM YYYY")}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-foreground">
                            {currency}
                            {toFixedDisplay(savings.amount)}
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-medium text-primary">
                            {savings.shared || savings.users.length > 0 ? (
                              <span className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                                Shared
                              </span>
                            ) : null}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            Created:{" "}
                            {dayjs(savings.createdAt).format("DD MMM YYYY")}
                          </p>
                        </div>
                        {savings.username === user?.username && (
                          <Flex gap={20}>
                            <EditFilled
                              style={{ color: "grey" }}
                              onClick={() => setEditSavingsRef(savings)}
                            />
                            <DeleteFilled
                              style={{ color: "grey" }}
                              onClick={() => setDeletableSavingsId(savings.id)}
                            />
                          </Flex>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Savings Goals */}
          <SavingsGoalsSection />

          {/* Live Currency Rates */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Live Currency Rates
              </CardTitle>
              <CardDescription>Exchange rates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {isCurrencyRatesDataLoading && <Spin size="large" />}
                {currencyRatesData && (
                  <>
                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            USD
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("USD")}{" "}
                            {toFixedDisplay(
                              currencyRatesData.USD *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            GBP
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("GBP")}{" "}
                            {toFixedDisplay(
                              currencyRatesData.GBP *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            CAD
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("CAD")}{" "}
                            {toFixedDisplay(
                              currencyRatesData.CAD *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            AUD
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("AUD")}{" "}
                            {toFixedDisplay(
                              currencyRatesData.AUD *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            SGD
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("SGD")}{" "}
                            {toFixedDisplay(
                              currencyRatesData.SGD *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            INR
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("INR")}{" "}
                            {new Intl.NumberFormat("en-IN", {
                              maximumFractionDigits: 2,
                            }).format(
                              currencyRatesData.INR *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            EUR
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("EUR")}{" "}
                            {toFixedDisplay(
                              currencyRatesData.EUR *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            HKD
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("HKD")}{" "}
                            {toFixedDisplay(
                              currencyRatesData.HKD *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-lg text-foreground">
                            AED
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getCurrencySymbolPerCountry("UAE")}{" "}
                            {toFixedDisplay(
                              currencyRatesData.AED *
                                (savingsTotal?.total ?? 0),
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
